/**
 * Defensive mappers for the Compliance self-service endpoints — Investigations, Corrective
 * Actions, and the capabilities of a Disciplinary case.
 *
 * Mapping only — no API calls, no DOM, no state (precedent: `events.ts`). The requests live
 * in `investigationsService` / `correctiveActionsService` (`services/erpat.ts`); this module
 * turns what they receive into the view models in `@/types/compliance`.
 *
 * This file must NEVER import `./models`: `models.ts` imports `mapCorrectiveActionRow`,
 * `mapDisciplinaryCapabilities` and the small readers (`text`, `idText`, `flag`, `dateLabel`)
 * from here for `mapDisciplinaryDetail`, so the reverse edge would be an import cycle.
 *
 * ERPat Compliance 1.6.0's End-User API (modules/Compliance/specs/
 * employee-self-service-implementation-plan.md §3) sends the same small records inside a
 * case, an investigation and a corrective action: a person, a link to a case or to an
 * investigation, a note of a notes thread, a code with its label. Each is read here once,
 * tolerantly: a missing or misspelt field degrades to an empty value, never a throw, so a
 * server a version behind still renders what it does send. What the caller may do is the
 * server's `capabilities`, read here and nowhere derived.
 *
 * Also here: the status words and the open/closed sets the screens' filter chips send (the
 * server's own vocabularies, §3), and the query/paging builders both lists share.
 */
import { asRecord, toList, type Json, type Pagination } from './http';
import { formatDate, formatRelative, toMillis } from '@/utils/format';
import type {
  CaseLink,
  CodeLabel,
  ComplianceNote,
  CorrectiveActionCapabilities,
  CorrectiveActionDetail,
  CorrectiveActionQuery,
  CorrectiveActionRole,
  CorrectiveActionSummary,
  CorrectiveActionVM,
  DisciplinaryCapabilities,
  InvestigationCapabilities,
  InvestigationDetail,
  InvestigationLink,
  InvestigationQuery,
  InvestigationVM,
  PersonRef,
} from '@/types/compliance';

/** Records per page: the server's default page size (it caps `per_page` at 100). */
export const COMPLIANCE_PER_PAGE = 25;

/** The longest search the server reads (it uses the first 100 characters). */
export const COMPLIANCE_SEARCH_MAX = 100;

/** The longest reply or note the server accepts, after trimming. */
export const COMPLIANCE_NOTE_MAX = 5000;

/* ── Readers ──────────────────────────────────────────────────────────────── */

/** A trimmed string, or '' (a finite number is written out). */
export function text(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

/** An id as a string: a positive integer or a non-empty string, else ''. */
export function idText(value: unknown): string {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  return text(value);
}

/** True only for an explicit yes (`true`, 1, '1', 'true'). */
export function flag(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true';
}

/** A whole count, 0 or more; anything else is 0. */
export function count(value: unknown): number {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isInteger(n) && n >= 0 ? n : 0;
}

/** A value that starts with a calendar day (`2026-10-18`, with or without a time after it). */
const DAY_PREFIX_RE = /^\d{4}-\d{2}-\d{2}/;

/**
 * A calendar date's label ("Oct 05, 2026"), or '' when there is none.
 *
 * The server writes a tenant-local `Y-m-d H:i:s` for its timestamps. Those carry no zone,
 * so only the day part is read: a full parse would let the device's zone move the day.
 */
export function dateLabel(value: unknown): string {
  const t = text(value);
  if (!t) return '';
  return formatDate(DAY_PREFIX_RE.test(t) ? t.slice(0, 10) : value);
}

/** A timestamp's relative label ("2h ago", "Sep 12"), or ''. */
export function relativeLabel(value: unknown): string {
  return text(value) ? formatRelative(value) : '';
}

/** `on_hold` → "On hold": the words for a code the server sent no label for. */
export function humanize(code: string): string {
  const words = code.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1).toLowerCase() : '';
}

/** `{ id, name }`, or null when the server sent no person (or one without a name). */
export function mapPerson(value: unknown): PersonRef | null {
  if (value === null || value === undefined) return null;
  const r = asRecord(value);
  const name = text(r.name);
  if (!name) return null;
  return { id: idText(r.id), name };
}

/** `[{ code, label }]`, dropping entries without a code; a missing label is the code's words. */
export function mapCodeLabels(value: unknown): CodeLabel[] {
  return toList<unknown>(value)
    .map((entry) => {
      const r = asRecord(entry);
      const code = text(r.code);
      return code ? { code, label: text(r.label) || humanize(code) } : null;
    })
    .filter((entry): entry is CodeLabel => entry !== null);
}

/** The words for a case status (`CaseLink` carries no label). */
export const CASE_STATUS_LABELS: Readonly<Record<string, string>> = {
  new: 'New',
  open: 'Open',
  on_hold: 'On hold',
  closed: 'Closed',
};

export function caseStatusLabel(status: string): string {
  return CASE_STATUS_LABELS[status] ?? (humanize(status) || 'Open');
}

/** A case summary, or null when the record has none. */
export function mapCaseLink(value: unknown): CaseLink | null {
  if (value === null || value === undefined) return null;
  const r = asRecord(value);
  const id = idText(r.id);
  if (!id) return null;
  const status = text(r.status).toLowerCase();
  return {
    id,
    categoryName: text(r.category_name) || text(r.label) || 'Disciplinary case',
    status,
    statusLabel: caseStatusLabel(status),
    dateOccurred: text(r.date_occurred),
    dateLabel: dateLabel(r.date_occurred),
    isMine: flag(r.is_mine),
  };
}

/** The words for an investigation phase, when the server sent none. */
export const PHASE_LABELS: Readonly<Record<string, string>> = {
  planning: 'Planning',
  interviews: 'Interviews',
  evidence_review: 'Evidence review',
  findings_review: 'Findings review',
  closed: 'Closed',
};

export function phaseLabel(phase: string, given?: unknown): string {
  return text(given) || PHASE_LABELS[phase] || humanize(phase);
}

/** An investigation summary, or null when the record has none (or the caller may not see it). */
export function mapInvestigationLink(value: unknown): InvestigationLink | null {
  if (value === null || value === undefined) return null;
  const r = asRecord(value);
  const id = idText(r.id);
  if (!id) return null;
  const phase = text(r.phase).toLowerCase();
  return {
    id,
    reference: text(r.reference),
    title: text(r.title),
    phase,
    phaseLabel: phaseLabel(phase, r.phase_label),
    isMine: flag(r.is_mine),
  };
}

/** One note of a thread. */
export function mapNote(value: unknown, index = 0): ComplianceNote {
  const r = asRecord(value);
  const at = r.created_at;
  return {
    id: idText(r.id) || `note-${index}`,
    kind: text(r.kind) === 'acknowledgement' ? 'acknowledgement' : 'note',
    body: text(r.body),
    author: mapPerson(r.author) ?? { id: '', name: 'Someone' },
    isMine: flag(r.is_mine),
    createdAt: text(at),
    sentAt: text(at) ? toMillis(at) : 0,
    timeLabel: relativeLabel(at),
  };
}

/** A thread, oldest first as the server sends it. */
export function mapNotes(value: unknown): ComplianceNote[] {
  return toList<unknown>(value).map((note, index) => mapNote(note, index));
}

/**
 * A list's paging, read from its `meta` (`{ page, per_page, total }`). Without a total,
 * a full page means there may be another: the list asks once more, and a page that brings
 * nothing new ends it (useServerPagedList's end latch).
 */
export function readPagination(data: unknown, page: number, perPage: number, itemCount: number): Pagination {
  const meta = asRecord(asRecord(data).meta);
  const served = count(meta.per_page) || perPage;
  const loaded = (page - 1) * served + itemCount;
  const total = typeof meta.total === 'number' && Number.isFinite(meta.total) ? meta.total : itemCount >= served ? loaded + 1 : loaded;
  return {
    page: count(meta.page) || page,
    perPage: served,
    total,
    totalPages: Math.max(1, Math.ceil(total / served)),
  };
}

/** The list rows under `key`, or under the first array the payload holds. */
export function rowsOf(data: unknown, key: string): Json[] {
  const rows = asRecord(data)[key];
  return Array.isArray(rows) ? (rows as Json[]) : toList<Json>(data);
}

/** The record under `key` (`{ investigation: {...} }`); throws when the server sent none. */
export function recordOf(data: unknown, key: string, what: string): Json {
  const nested = asRecord(data)[key];
  const record = nested && typeof nested === 'object' && !Array.isArray(nested) ? (nested as Json) : asRecord(data);
  if (!idText(record.id)) throw new Error(`The server did not return the ${what}.`);
  return record;
}

/** A page number of 1 or more. */
export function pageOf(value: number | undefined): number {
  return Math.max(1, Math.floor(Number.isFinite(value) ? (value as number) : 1));
}

/** The status filter as the server reads it: a comma-separated list, or nothing for every status. */
export function csv(values: readonly string[] | undefined): string {
  return (values ?? []).filter(Boolean).join(',');
}

/** The search as the server reads it: trimmed, at most {@link COMPLIANCE_SEARCH_MAX} characters. */
export function searchTerm(value: string | undefined): string {
  // Cut by code point, as the server counts (mb_substr): a UTF-16 slice could split an emoji
  // and send half of it, which the server reads as a replacement character.
  return Array.from((value ?? '').trim()).slice(0, COMPLIANCE_SEARCH_MAX).join('');
}

/* ── Status filters ──────────────────────────────────────────────────────── */

/** A case still open (Open chip); `closed` is the Closed chip. */
export const OPEN_CASE_STATUSES: readonly string[] = ['new', 'open', 'on_hold'];
export const CLOSED_CASE_STATUSES: readonly string[] = ['closed'];

/** An investigation's open phases (Open chip), and the closed one. */
export const OPEN_PHASES: readonly string[] = ['planning', 'interviews', 'evidence_review', 'findings_review'];
export const CLOSED_PHASES: readonly string[] = ['closed'];

/** A corrective action still open (Open chip), and the two closing statuses. */
export const OPEN_ACTION_STATUSES: readonly string[] = ['in_progress', 'overdue', 'on_track', 'review_due'];
export const CLOSED_ACTION_STATUSES: readonly string[] = ['verified', 'closed'];

/* ── Investigations ──────────────────────────────────────────────────────── */

/** A list row (also a detail's head). */
export function mapInvestigationRow(value: unknown, index = 0): InvestigationVM {
  const r = asRecord(value);
  const phase = text(r.phase).toLowerCase();
  const category = text(r.category);
  return {
    id: idText(r.id) || `investigation-${index}`,
    reference: text(r.reference),
    title: text(r.title) || 'Investigation',
    category,
    categoryLabel: text(r.category_label) || humanize(category),
    phase,
    phaseLabel: phaseLabel(phase, r.phase_label),
    isClosed: phase === 'closed',
    targetReviewDate: text(r.target_review_date),
    targetReviewLabel: dateLabel(r.target_review_date),
    closedAt: text(r.closed_at),
    closedLabel: dateLabel(r.closed_at),
    location: text(r.location),
    highSensitivity: flag(r.high_sensitivity),
    legalHold: flag(r.legal_hold),
    restricted: flag(r.restricted),
    evidenceCount: count(r.evidence_count),
    interviewsScheduled: count(r.interviews_scheduled),
    correctiveActionCount: count(r.corrective_action_count),
    linkedCase: mapCaseLink(r.linked_case),
    createdAt: text(r.created_at),
    updatedAt: text(r.updated_at),
    updatedLabel: relativeLabel(r.updated_at),
  };
}

export function mapCorrectiveActionSummary(value: unknown, index = 0): CorrectiveActionSummary {
  const r = asRecord(value);
  const status = text(r.status).toLowerCase();
  return {
    id: idText(r.id) || `action-${index}`,
    reference: text(r.reference),
    title: text(r.title) || 'Corrective action',
    typeLabel: text(r.type_label),
    status,
    statusLabel: text(r.status_label) || humanize(status),
    nextDueDate: text(r.next_due_date),
    nextDueLabel: dateLabel(r.next_due_date),
    employee: mapPerson(r.employee),
    owner: mapPerson(r.owner),
    isMine: flag(r.is_mine),
  };
}

/** An investigation's `capabilities`; absent (a server a version behind) means nothing is offered. */
function mapInvestigationCapabilities(value: unknown): InvestigationCapabilities {
  const r = asRecord(value);
  return {
    canChangePhase: r.can_change_phase === true,
    canUpdateProgress: r.can_update_progress === true,
    canAddNote: r.can_add_note === true,
    phases: mapCodeLabels(r.phases),
  };
}

/** `{ investigation: Detail }` → the detail. Throws when the server sent no investigation. */
export function mapInvestigationDetail(data: unknown): InvestigationDetail {
  const r: Json = recordOf(data, 'investigation', 'investigation');
  return {
    ...mapInvestigationRow(r),
    description: text(r.description),
    lead: mapPerson(r.lead),
    externalLeadName: text(r.external_lead_name),
    correctiveActions: toList<unknown>(r.corrective_actions).map((row, i) => mapCorrectiveActionSummary(row, i)),
    notes: mapNotes(r.notes),
    capabilities: mapInvestigationCapabilities(r.capabilities),
  };
}

/** The query string of `GET /me/investigations`: `{ page, per_page, phase?, search? }`. */
export function investigationListParams(query: InvestigationQuery = {}): Record<string, string | number> {
  const params: Record<string, string | number> = { page: pageOf(query.page), per_page: COMPLIANCE_PER_PAGE };
  const phase = csv(query.phases);
  if (phase) params.phase = phase;
  const search = searchTerm(query.search);
  if (search) params.search = search;
  return params;
}

/** One page of `GET /me/investigations`, as the list takes it. */
export function mapInvestigationPage(data: unknown, page: number): { items: InvestigationVM[]; pagination: Pagination } {
  const at = pageOf(page);
  const offset = (at - 1) * COMPLIANCE_PER_PAGE;
  const items = rowsOf(data, 'investigations').map((row, i) => mapInvestigationRow(row, offset + i));
  return { items, pagination: readPagination(data, at, COMPLIANCE_PER_PAGE, items.length) };
}

/* ── Corrective actions ──────────────────────────────────────────────────── */

/** The roles the server names, in a fixed order (owner first). */
function rolesOf(value: unknown): CorrectiveActionRole[] {
  const given = new Set(Array.isArray(value) ? value.map((v) => text(v)) : []);
  return (['owner', 'employee'] as const).filter((role) => given.has(role));
}

/** A list row (also a detail's head, and a row inside a case's detail). */
export function mapCorrectiveActionRow(value: unknown, index = 0): CorrectiveActionVM {
  const r = asRecord(value);
  const status = text(r.status).toLowerCase();
  const type = text(r.type);
  return {
    id: idText(r.id) || `action-${index}`,
    reference: text(r.reference),
    title: text(r.title) || 'Corrective action',
    type,
    typeLabel: text(r.type_label) || humanize(type),
    status,
    statusLabel: text(r.status_label) || humanize(status) || 'In progress',
    isClosed: CLOSED_ACTION_STATUSES.includes(status),
    nextDueDate: text(r.next_due_date),
    nextDueLabel: dateLabel(r.next_due_date),
    effectivenessReviewDate: text(r.effectiveness_review_date),
    effectivenessReviewLabel: dateLabel(r.effectiveness_review_date),
    expiryDate: text(r.expiry_date),
    expiryLabel: dateLabel(r.expiry_date),
    closedDate: text(r.closed_date),
    closedLabel: dateLabel(r.closed_date),
    effectivenessVerified: flag(r.effectiveness_verified),
    myRoles: rolesOf(r.my_roles),
    employee: mapPerson(r.employee),
    owner: mapPerson(r.owner),
    acknowledgedAt: text(r.acknowledged_at),
    acknowledgedLabel: dateLabel(r.acknowledged_at),
    linkedCase: mapCaseLink(r.linked_case),
    linkedInvestigation: mapInvestigationLink(r.linked_investigation),
    createdAt: text(r.created_at),
    updatedAt: text(r.updated_at),
    updatedLabel: relativeLabel(r.updated_at),
  };
}

/** A corrective action's `capabilities`; absent (a server a version behind) means nothing is offered. */
function mapCorrectiveActionCapabilities(value: unknown): CorrectiveActionCapabilities {
  const r = asRecord(value);
  return {
    canChangeStatus: r.can_change_status === true,
    canUpdateDates: r.can_update_dates === true,
    canAcknowledge: r.can_acknowledge === true,
    canAddNote: r.can_add_note === true,
    statuses: mapCodeLabels(r.statuses),
  };
}

/** `{ corrective_action: Detail }` → the detail. Throws when the server sent no action. */
export function mapCorrectiveActionDetail(data: unknown): CorrectiveActionDetail {
  const r: Json = recordOf(data, 'corrective_action', 'corrective action');
  return {
    ...mapCorrectiveActionRow(r),
    description: text(r.description),
    notes: mapNotes(r.notes),
    capabilities: mapCorrectiveActionCapabilities(r.capabilities),
  };
}

/** The query string of `GET /me/corrective-actions`: `{ page, per_page, role?, status?, linked_case_id?, search? }`. */
export function correctiveActionListParams(query: CorrectiveActionQuery = {}): Record<string, string | number> {
  const params: Record<string, string | number> = { page: pageOf(query.page), per_page: COMPLIANCE_PER_PAGE };
  if (query.role) params.role = query.role;
  const status = csv(query.statuses);
  if (status) params.status = status;
  if (query.linkedCaseId) params.linked_case_id = query.linkedCaseId;
  const search = searchTerm(query.search);
  if (search) params.search = search;
  return params;
}

/** One page of `GET /me/corrective-actions`, as the list takes it. */
export function mapCorrectiveActionPage(data: unknown, page: number): { items: CorrectiveActionVM[]; pagination: Pagination } {
  const at = pageOf(page);
  const offset = (at - 1) * COMPLIANCE_PER_PAGE;
  const items = rowsOf(data, 'corrective_actions').map((row, i) => mapCorrectiveActionRow(row, offset + i));
  return { items, pagination: readPagination(data, at, COMPLIANCE_PER_PAGE, items.length) };
}

/* ── Disciplinary ────────────────────────────────────────────────────────── */

/** A case's `capabilities`; absent (a server before Compliance 1.6.0) means nothing is offered. */
export function mapDisciplinaryCapabilities(value: unknown): DisciplinaryCapabilities {
  const r = asRecord(value);
  return {
    canReply: r.can_reply === true,
    canAcknowledge: r.can_acknowledge === true,
    quickReplies: toList<unknown>(r.quick_replies)
      .map((q) => text(q))
      .filter(Boolean),
    acknowledgeLabel: text(r.acknowledge_label) || 'Acknowledge',
  };
}
